import { ReportPdfData } from '../types';
import { createEyebrow, createFooter } from '../utils';
import { PdfAssets } from '../../pdfTemplates';

/**
 * Creates the architecture overview page for the PDF report
 * @param data Report data
 * @param diagramImageBase64 Base64 encoded image of the diagram
 * @param assets Logo and font assets for the PDF
 * @param colors Color palette for styling
 * @param pageNumber Current page number
 * @param totalPages Total number of pages
 * @returns Architecture overview page content for pdfmake
 */
export const createArchitectureOverviewPage = (
  data: ReportPdfData,
  diagramImageBase64: string | undefined,
  assets: PdfAssets | undefined,
  colors: Record<string, string>,
  pageNumber: number,
  totalPages: number
) => {
  return [
    // Eyebrow section
    ...createEyebrow('Architecture Overview', assets?.markUrl, colors),
    
    // Content section
    {
      stack: [
        {
          text: 'Architecture Overview',
          fontSize: 22,
          bold: true,
          color: colors.black,
          margin: [64, 48, 0, 24]
        },
        
        // Diagram section
        {
          stack: [
            diagramImageBase64 ? {
              image: diagramImageBase64.startsWith('data:') ? diagramImageBase64 : `data:image/png;base64,${diagramImageBase64}`,
              width: 467, // 595 - 64 - 64 (page width minus margins)
              fit: [467, 240],
              alignment: 'left',
              margin: [0, 0, 0, 8],
              background: colors.bgGrey
            } : {
              // Placeholder rectangle if no image is provided
              canvas: [
                {
                  type: 'rect',
                  x: 0,
                  y: 0,
                  w: 467,
                  h: 240,
                  color: colors.bgGrey
                }
              ],
              margin: [0, 0, 0, 8]
            },
            
            // Link to project
            {
              columns: [
                {
                  text: 'View in Architect:',
                  fontSize: 9,
                  color: colors.darkGrey,
                  width: 'auto',
                  margin: [0, 0, 4, 0]
                },
                data.shareLink ? {
                  text: data.projectName,
                  fontSize: 9,
                  color: colors.primaryDark,
                  decoration: 'underline',
                  link: data.shareLink,
                  width: 'auto'
                } : {
                  text: 'Link not available',
                  fontSize: 9,
                  color: colors.mediumGrey,
                  width: 'auto'
                }
              ],
              columnGap: 0,
              margin: [0, 0, 0, 0]
            }
          ],
          margin: [64, 0, 64, 40]
        },
        
        // Two column section
        {
          columns: [
            {
              stack: [
                {
                  text: 'Infrastructure Overview',
                  fontSize: 14,
                  bold: true,
                  color: colors.black,
                  margin: [0, 0, 0, 8]
                },
                {
                  text: 'MetaRouter provides a server-side data infrastructure that centralizes your customer data collection and distribution, ensuring data quality, privacy compliance, and optimal performance.',
                  fontSize: 9,
                  color: colors.darkGrey,
                  margin: [0, 0, 0, 0]
                }
              ],
              width: '50%',
              margin: [0, 0, 12, 0]
            },
            {
              stack: [
                {
                  text: 'Key Benefits',
                  fontSize: 14,
                  bold: true,
                  color: colors.black,
                  margin: [0, 0, 0, 8]
                },
                {
                  text: 'Improved website performance, enhanced data security, better compliance with privacy regulations, and more accurate analytics by bypassing ad blockers and browser limitations.',
                  fontSize: 9,
                  color: colors.darkGrey,
                  margin: [0, 0, 0, 0]
                }
              ],
              width: '50%',
              margin: [12, 0, 0, 0]
            }
          ],
          margin: [64, 0, 64, 0]
        }
      ]
    },
    
    // Footer
    createFooter(pageNumber, totalPages, colors),
    { text: '', pageBreak: 'after' }
  ];
};
